'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

const SLOT_STATE_ATTRIBUTE = 'data-roster-chrome-slot';

/**
 * A chrome target that admits moved controls only once React has hydrated it.
 * Dispatch streams its board under Suspense, and React hydrates each dehydrated
 * boundary in a later pass than the shell. Moving a control into a target that
 * is still dehydrated adds a child the server never rendered, so hydrating that
 * boundary fails with React error #418 (FU-289). The server and the first client
 * render both say "pending"; the slot says "ready" only after its own hydration.
 */
export function RosterChromeSlot({
  id,
  className,
  as: Element = 'div',
}: {
  id: string;
  className?: string;
  as?: 'div' | 'span';
}) {
  const [ready, setReady] = useState(false);
  useLayoutEffect(() => setReady(true), []);
  return (
    <Element id={id} className={className} data-roster-chrome-slot={ready ? 'ready' : 'pending'} />
  );
}

export function RosterAttentionPortal({ children }: { children: ReactNode }) {
  return <RosterChromePortal targetId="roster-shell-attention">{children}</RosterChromePortal>;
}
export function RosterToolbarPortal({ children }: { children: ReactNode }) {
  return (
    <RosterChromePortal targetId="roster-board-secondary-controls">{children}</RosterChromePortal>
  );
}
export function RosterDateToolbarPortal({ children }: { children: ReactNode }) {
  return <RosterChromePortal targetId="roster-board-date-controls">{children}</RosterChromePortal>;
}
export function RosterExceptionsPortal({ children }: { children: ReactNode }) {
  return <RosterChromePortal targetId="roster-exceptions-action">{children}</RosterChromePortal>;
}
export function RosterPlannerNavigationPortal({ children }: { children: ReactNode }) {
  return <RosterChromePortal targetId="roster-planner-navigation">{children}</RosterChromePortal>;
}

/** Move the existing DOM host after hydration rather than switching React roots.
 * Children stay mounted, including their focus, effects and disclosure state.
 * A missing shell target retains the server-rendered inline fallback. */
function RosterChromePortal({ children, targetId }: { children: ReactNode; targetId: string }) {
  const host = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const element = host.current;
    const parent = element?.parentNode;
    if (!element || !parent) return;
    const attach = () => {
      const found = document.getElementById(targetId);
      // A RosterChromeSlot that has not hydrated yet is not a target: joining it
      // would make its server markup disagree with hydration (FU-289).
      const target = found?.getAttribute(SLOT_STATE_ATTRIBUTE) === 'pending' ? null : found;
      // Streaming/navigation can replace a target after the first attachment.
      // Recover to the inline host while it is absent; never leave controls in
      // a detached target. Avoid moving an already attached host on mutations.
      const destination = target ?? parent;
      if (element.parentNode !== destination) destination.appendChild(element);
      if (parent instanceof HTMLElement) parent.hidden = Boolean(target);
    };
    // Keep observing slot replacement as well as late arrival. This also
    // survives Strict Mode's setup/cleanup cycle without remounting children.
    const observer = new MutationObserver(attach);
    attach();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [SLOT_STATE_ATTRIBUTE],
    });
    return () => {
      observer.disconnect();
      parent.appendChild(element);
      if (parent instanceof HTMLElement) parent.hidden = false;
    };
  }, [targetId]);
  return (
    <span className="contents [&[hidden]]:hidden">
      <span ref={host} className="contents">
        {children}
      </span>
    </span>
  );
}
